import React from 'react'
import { MenuItem, TextField } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'

// The list toolbars used bare `<input className="form-control">` and, next to a
// Material Pagination, a `<select>` with no class at all. These three cover every
// filter control on the list pages so the toolbars cannot drift apart again, and
// so the row reads as one component set with the pagination below it.
//
// MUI v4 dims placeholders to 0.42 opacity — about 2.4:1 on white, under the 4.5:1
// floor, and Metronic's own #B5B5C3 placeholder measures 2.3:1, so neither was
// readable. Full opacity at 60% black measures 5.7:1 and still reads as a hint
// against the 87% black the field's own value uses.
const useStyles = makeStyles({
  field: {
    '& input::placeholder': { opacity: 1, color: 'rgba(0, 0, 0, 0.6)' },
  },
})

export function SearchField({ placeholder, value, onChange, onSearch, disabled = false }) {
  const classes = useStyles()
  return (
    <TextField
      className={classes.field}
      variant="outlined"
      size="small"
      fullWidth
      placeholder={placeholder}
      disabled={disabled}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      onKeyDown={(event) => {
        if (!disabled && event.key === 'Enter') onSearch()
      }}
    />
  )
}

// Clearing started life as an X inside the field, which only appears once you have
// typed — so an operator opening the page never saw one and could not tell the
// feature existed. This sits beside Search instead, where they are already looking,
// and clears the whole filter set rather than one field. Hidden while every filter
// is at its default: nothing to clear, so nothing to press.
export function ClearFiltersButton({ show, onClear, label = 'ล้าง' }) {
  if (!show) return null
  return (
    <button type="button" className="btn btn-light" style={{ width: '100%' }} onClick={onClear}>
      {label}
    </button>
  )
}

// Offered on both User Management and Participants Management, so like MfaBadge the
// two cannot drift apart on wording. The values are the strings the API expects.
export const MFA_FILTER_OPTIONS = [
  { value: '', label: 'MFA: ทั้งหมด' },
  { value: '1', label: 'MFA: ผูกแล้ว' },
  { value: '0', label: 'MFA: ยังไม่ผูก' },
]

// `options` is [{ value, label, disabled }] — disabled carries the "Please
// Select ..." row on Report, which has to stay pickable-looking but unpickable.
// onChange is handed the raw event, so the page handlers that already read
// event.target.value need no change.
export function FilterSelect({ value, onChange, options, disabled, width }) {
  return (
    <TextField
      select
      variant="outlined"
      size="small"
      fullWidth={!width}
      style={width ? { width } : undefined}
      value={value}
      onChange={onChange}
      disabled={disabled}
      // Without displayEmpty a Select holding '' renders an empty box and ignores
      // the MenuItem that carries that exact value — so "MFA: ทั้งหมด" only appeared
      // after the operator had picked something else and come back.
      SelectProps={{ displayEmpty: true }}
    >
      {options.map((option) => (
        <MenuItem key={option.value} value={option.value} disabled={option.disabled}>
          {option.label}
        </MenuItem>
      ))}
    </TextField>
  )
}

const PAGE_SIZES = [10, 25, 50, 100]

// Sits under the table beside the pagination, so it is sized to its four values
// rather than stretched, and the label stays on the same line as the control.
export function PageSizeSelect({ label, value, onChange, disabled }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
      {label}
      <TextField
        select
        variant="outlined"
        size="small"
        value={value}
        onChange={onChange}
        disabled={disabled}
        style={{ width: 88 }}
      >
        {PAGE_SIZES.map((size) => (
          <MenuItem key={size} value={size}>
            {size}
          </MenuItem>
        ))}
      </TextField>
    </span>
  )
}
